import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { format } from 'date-fns';
import { 
  ArrowLeft, Calendar, Clock, User, MapPin, 
  Users, Check, AlertCircle, Repeat, Sparkles, DollarSign
} from 'lucide-react';
import { formatFullName, formatDate, formatCurrency, isCustomEstimate as checkCustom } from '../utils/helpers';
import { cleaningFrequencies } from '../utils/estimateData';
import './EstimateSchedule.css';

function EstimateSchedule() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    getEstimateById, getClientById, getPropertyById, 
    convertEstimateToJob, state, dispatch 
  } = useApp();

  const estimate = getEstimateById(id);
  const client = estimate ? getClientById(estimate.clientId) : null;
  const property = estimate ? getPropertyById(estimate.propertyId) : null;
  const frequencyConfig = cleaningFrequencies[estimate?.calculatorInputs?.frequency];

  // Check if this is a custom estimate
  const isCustom = checkCustom(estimate);

  // Pricing - handle both residential and custom estimates
  const deepCleanPrice = estimate?.approvedDeepCleanTotal || estimate?.deepCleanTotal || 0;
  const recurringPrice = estimate?.approvedRecurringTotal || estimate?.recurringTotal || 0;
  const customTotal = estimate?.total || 0;
  
  // For custom estimates, use the total; for residential, use deep clean or recurring
  const jobPrice = isCustom ? customTotal : (deepCleanPrice || recurringPrice);
  const hasDeepClean = !isCustom && deepCleanPrice > 0;
  const isRecurringService = !isCustom && estimate?.calculatorInputs?.frequency && estimate?.calculatorInputs?.frequency !== 'one_time';

  // Scheduling state
  const [scheduleData, setScheduleData] = useState({
    startDate: estimate?.preferredDate || '',
    startTime: '09:00',
    duration: estimate?.calculatorInputs?.estimatedHours || 3,
    assignedTeam: [],
    recurringSchedule: isRecurringService, // Default to true for recurring services
    // For custom estimates, allow setting recurring manually
    customRecurringFrequency: 'one_time',
    notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get available employees
  const availableEmployees = state.employees.filter(e => e.status === 'active');

  if (!estimate) {
    return (
      <div className="schedule-page">
        <div className="not-found">
          <h2>Estimate not found</h2>
          <Link to="/admin/estimates" className="btn btn-primary">Back to Estimates</Link>
        </div>
      </div>
    );
  }

  if (estimate.status !== 'approved') {
    return (
      <div className="schedule-page">
        <div className="not-found">
          <AlertCircle size={48} />
          <h2>Cannot Schedule</h2>
          <p>Only approved estimates can be scheduled.</p>
          <Link to={`/admin/estimates/${estimate.id}`} className="btn btn-primary">
            Back to Estimate
          </Link>
        </div>
      </div>
    );
  }

  const handleInputChange = (field, value) => {
    setScheduleData(prev => ({ ...prev, [field]: value }));
  };

  const toggleTeamMember = (employeeId) => {
    setScheduleData(prev => ({
      ...prev,
      assignedTeam: prev.assignedTeam.includes(employeeId)
        ? prev.assignedTeam.filter(id => id !== employeeId)
        : [...prev.assignedTeam, employeeId]
    }));
  };

  const handleSchedule = async () => {
    if (!scheduleData.startDate || scheduleData.assignedTeam.length === 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Calculate end time
      const [hours, minutes] = scheduleData.startTime.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes + (scheduleData.duration * 60);
      const endHours = Math.floor(totalMinutes / 60);
      const endMins = totalMinutes % 60;
      const endTime = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;

      // Get team member names
      const teamNames = scheduleData.assignedTeam.map(empId => {
        const emp = state.employees.find(e => e.id === empId);
        return emp ? `${emp.firstName} ${emp.lastName?.charAt(0) || ''}.` : '';
      }).filter(Boolean);

      // Create the job from estimate
      const job = await convertEstimateToJob(estimate.id, {
        date: scheduleData.startDate,
        time: scheduleData.startTime,
        endTime: endTime,
        team: scheduleData.assignedTeam,
        teamNames: teamNames,
        isRecurring: isCustom 
          ? scheduleData.customRecurringFrequency !== 'one_time'
          : scheduleData.recurringSchedule,
        customRecurringFrequency: scheduleData.customRecurringFrequency,
        notes: scheduleData.notes,
        isCustomEstimate: isCustom,
        customPrice: isCustom ? customTotal : null
      });

      // Navigate to dispatch page with the scheduled date
      let successMessage;
      if (isCustom) {
        successMessage = scheduleData.customRecurringFrequency !== 'one_time'
          ? `Job scheduled for ${formatDate(scheduleData.startDate)}! Recurring appointments will be created.`
          : `Job scheduled for ${formatDate(scheduleData.startDate)}!`;
      } else {
        successMessage = scheduleData.recurringSchedule
          ? `Deep clean scheduled for ${formatDate(scheduleData.startDate)}! Recurring ${frequencyConfig?.label} appointments created.`
          : `Job scheduled for ${formatDate(scheduleData.startDate)}!`;
      }
      
      navigate('/admin/dispatch', { 
        state: { 
          message: successMessage,
          jobId: job?.id 
        }
      });
    } catch (error) {
      console.error('Error scheduling job:', error);
      alert('Error scheduling job. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSchedule = scheduleData.startDate && scheduleData.assignedTeam.length > 0;

  return (
    <div className="schedule-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(`/admin/estimates/${estimate.id}`)}>
          <ArrowLeft size={20} />
        </button>
        <div className="header-content">
          <h1>Schedule Job</h1>
          <p className="header-subtitle">Create a job from approved estimate</p>
        </div>
      </div>

      <div className="schedule-layout">
        {/* Left: Scheduling Form */}
        <div className="schedule-form">
          {/* Client & Property Summary */}
          <div className="schedule-card summary-card">
            <div className="summary-row">
              <div className="summary-item">
                <User size={18} />
                <div>
                  <label>Client</label>
                  <p>{client ? formatFullName(client) : estimate?.clientName || 'Unknown'}</p>
                </div>
              </div>
              <div className="summary-item">
                <MapPin size={18} />
                <div>
                  <label>Property</label>
                  <p>{property?.addressLine1 || estimate?.propertyAddress || estimate?.propertyInfo?.street || 'No address'}</p>
                  <span className="meta">
                    {property?.city || estimate?.propertyInfo?.city || ''}, {property?.state || estimate?.propertyInfo?.state || 'VA'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Summary */}
          <div className="schedule-card pricing-card">
            <h3><DollarSign size={20} /> Service Pricing</h3>
            
            {isCustom ? (
              <>
                {/* Custom Estimate - Show line items */}
                <div className="custom-estimate-summary">
                  <div className="estimate-title">{estimate?.title || 'Custom Quote'}</div>
                  {estimate?.lineItems?.map((item, idx) => (
                    <div key={idx} className="line-item-row-small">
                      <span className="item-desc">{item.description}</span>
                      <span className="item-price">{formatCurrency(item.total || ((item.qty ?? item.quantity ?? 1) * item.unitPrice))}</span>
                    </div>
                  ))}
                  {estimate?.discountAmount > 0 && (
                    <div className="line-item-row-small discount">
                      <span className="item-desc">Discount</span>
                      <span className="item-price">-{formatCurrency(estimate.discountAmount)}</span>
                    </div>
                  )}
                </div>
                <div className="pricing-row total-row">
                  <div className="pricing-info">
                    <span className="pricing-label">Total</span>
                  </div>
                  <span className="pricing-amount">{formatCurrency(customTotal)}</span>
                </div>
              </>
            ) : (
              <>
                {hasDeepClean && (
                  <div className="pricing-row first-clean">
                    <div className="pricing-icon">
                      <Sparkles size={18} />
                    </div>
                    <div className="pricing-info">
                      <span className="pricing-label">First Visit - Deep Clean</span>
                      <span className="pricing-desc">One-time initial cleaning</span>
                    </div>
                    <span className="pricing-amount">{formatCurrency(deepCleanPrice)}</span>
                  </div>
                )}
                
                {isRecurringService && (
                  <div className="pricing-row recurring-clean">
                    <div className="pricing-icon recurring">
                      <Repeat size={18} />
                    </div>
                    <div className="pricing-info">
                      <span className="pricing-label">{frequencyConfig?.label} Cleaning</span>
                      <span className="pricing-desc">Ongoing service price</span>
                    </div>
                    <span className="pricing-amount">{formatCurrency(recurringPrice)}</span>
                  </div>
                )}

                {!isRecurringService && !hasDeepClean && (
                  <div className="pricing-row">
                    <div className="pricing-info">
                      <span className="pricing-label">One-Time Service</span>
                    </div>
                    <span className="pricing-amount">{formatCurrency(recurringPrice)}</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Date & Time */}
          <div className="schedule-card">
            <h3><Calendar size={20} /> Date & Time</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Start Date *</label>
                <input
                  type="date"
                  value={scheduleData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  min={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>
              <div className="form-group">
                <label>Start Time *</label>
                <select
                  value={scheduleData.startTime}
                  onChange={(e) => handleInputChange('startTime', e.target.value)}
                >
                  <option value="08:00">8:00 AM</option>
                  <option value="08:30">8:30 AM</option>
                  <option value="09:00">9:00 AM</option>
                  <option value="09:30">9:30 AM</option>
                  <option value="10:00">10:00 AM</option>
                  <option value="10:30">10:30 AM</option>
                  <option value="11:00">11:00 AM</option>
                  <option value="11:30">11:30 AM</option>
                  <option value="12:00">12:00 PM</option>
                  <option value="12:30">12:30 PM</option>
                  <option value="13:00">1:00 PM</option>
                  <option value="13:30">1:30 PM</option>
                  <option value="14:00">2:00 PM</option>
                </select>
              </div>
              <div className="form-group">
                <label>Estimated Duration</label>
                <select
                  value={scheduleData.duration}
                  onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
                >
                  <option value={2}>2 hours</option>
                  <option value={3}>3 hours</option>
                  <option value={4}>4 hours</option>
                  <option value={5}>5 hours</option>
                  <option value={6}>6 hours</option>
                  <option value={8}>8 hours (full day)</option>
                </select>
              </div>
            </div>

            {/* Recurring Option - Residential */}
            {!isCustom && isRecurringService && (
              <div className="recurring-option">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={scheduleData.recurringSchedule}
                    onChange={(e) => handleInputChange('recurringSchedule', e.target.checked)}
                  />
                  <span>Set up recurring schedule ({frequencyConfig?.label})</span>
                </label>
                {scheduleData.recurringSchedule && (
                  <div className="recurring-note success">
                    <Check size={16} />
                    <div>
                      <strong>Recurring jobs will be created automatically</strong>
                      <p>8 future {frequencyConfig?.label.toLowerCase()} appointments will be scheduled. More will be generated as needed.</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recurring Option - Custom Estimates */}
            {isCustom && (
              <div className="recurring-option">
                <label>Make this a recurring job?</label>
                <select
                  value={scheduleData.customRecurringFrequency}
                  onChange={(e) => handleInputChange('customRecurringFrequency', e.target.value)}
                  className="frequency-select"
                >
                  <option value="one_time">One-time job only</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every 2 Weeks</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
                {scheduleData.customRecurringFrequency !== 'one_time' && (
                  <div className="recurring-note success">
                    <Check size={16} />
                    <div>
                      <strong>Recurring jobs will be created</strong>
                      <p>Future appointments will be scheduled based on your selection.</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Team Assignment */}
          <div className="schedule-card">
            <h3><Users size={20} /> Assign Team *</h3>
            <p className="card-description">Select team members for this job</p>
            
            <div className="team-grid">
              {availableEmployees.map(employee => {
                const isSelected = scheduleData.assignedTeam.includes(employee.id);
                return (
                  <button
                    key={employee.id}
                    type="button"
                    className={`team-member ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleTeamMember(employee.id)}
                  >
                    <div className="member-avatar">
                      {employee.firstName[0]}{employee.lastName?.[0] || ''}
                    </div>
                    <div className="member-info">
                      <span className="member-name">{formatFullName(employee)}</span>
                      <span className="member-role">{employee.role}</span>
                    </div>
                    {isSelected && (
                      <div className="member-check">
                        <Check size={16} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {scheduleData.assignedTeam.length === 0 && (
              <p className="validation-message">
                <AlertCircle size={14} />
                Please select at least one team member
              </p>
            )}
          </div>

          {/* Notes */}
          <div className="schedule-card">
            <h3>Job Notes</h3>
            <textarea
              value={scheduleData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Add any special instructions for this job..."
              rows={4}
            />
          </div>
        </div>

        {/* Right: Preview & Actions */}
        <div className="schedule-sidebar">
          <div className="preview-card">
            <h3>Schedule Summary</h3>
            
            <div className="preview-item">
              <Calendar size={16} />
              <span>
                {scheduleData.startDate 
                  ? formatDate(scheduleData.startDate, 'EEEE, MMMM d, yyyy')
                  : 'Select a date'}
              </span>
            </div>
            
            <div className="preview-item">
              <Clock size={16} />
              <span>
                {scheduleData.startTime 
                  ? `${scheduleData.startTime} (${scheduleData.duration} hours)`
                  : 'Select a time'}
              </span>
            </div>
            
            <div className="preview-item">
              <Users size={16} />
              <span>
                {scheduleData.assignedTeam.length > 0
                  ? `${scheduleData.assignedTeam.length} team member${scheduleData.assignedTeam.length > 1 ? 's' : ''}`
                  : 'No team assigned'}
              </span>
            </div>

            {scheduleData.recurringSchedule && isRecurringService && (
              <div className="preview-item recurring">
                <Repeat size={16} />
                <span>Recurring {frequencyConfig?.label}</span>
              </div>
            )}

            {/* Pricing Summary in Sidebar */}
            <div className="preview-pricing">
              {hasDeepClean && (
                <div className="preview-price-row">
                  <span>First Visit (Deep Clean)</span>
                  <span>{formatCurrency(deepCleanPrice)}</span>
                </div>
              )}
              {scheduleData.recurringSchedule && isRecurringService && (
                <div className="preview-price-row recurring">
                  <span>{frequencyConfig?.label} (ongoing)</span>
                  <span>{formatCurrency(recurringPrice)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="action-buttons">
            <button 
              className="btn btn-secondary"
              onClick={() => navigate(`/admin/estimates/${estimate.id}`)}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary"
              onClick={handleSchedule}
              disabled={!canSchedule || isSubmitting}
            >
              {isSubmitting ? 'Scheduling...' : (
                scheduleData.recurringSchedule && isRecurringService
                  ? 'Schedule All Jobs'
                  : 'Schedule Job'
              )}
            </button>
          </div>

          {/* What Will Happen */}
          {scheduleData.recurringSchedule && isRecurringService && hasDeepClean && (
            <div className="what-happens">
              <h4>What will be created:</h4>
              <ul>
                <li>
                  <Sparkles size={14} />
                  <span>1 Deep Clean on {scheduleData.startDate ? formatDate(scheduleData.startDate) : 'selected date'}</span>
                </li>
                <li>
                  <Repeat size={14} />
                  <span>8 upcoming {frequencyConfig?.label.toLowerCase()} cleanings</span>
                </li>
                <li>
                  <Check size={14} />
                  <span>More jobs auto-generated as needed</span>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EstimateSchedule;
